import { useRef, useEffect, MutableRefObject } from "react";
import EditorJS, { OutputData } from "@editorjs/editorjs";
import { EDITOR_JS_TOOLS } from "./tools";

import { Button } from "antd";
import { BoxStyle } from "../../style/styleValue";

interface Props {
  postId: number;
  initialContent?: string;
  onSave: (content: string) => Promise<void>;
  // 바깥에서 작성 중인 내용을 읽을 때 (FreeText 전환 등)
  instanceRef?: MutableRefObject<EditorJS | null>;
}

const BasicTemplate: OutputData = {
  blocks: [],
  version: "2.29.0",
};

const Editor = ({ postId, initialContent, onSave, instanceRef }: Props) => {
  const editorInstance = useRef<EditorJS | null>(null);

  useEffect(() => {
    if (editorInstance.current) return;

    let initialData: OutputData = BasicTemplate;

    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        if (parsed && Array.isArray(parsed.blocks)) {
          initialData = parsed;
        }
      } catch (e) {
        console.error("JSON parsing error:", e);
      }
    }

    const editor = new EditorJS({
      holder: "editorJS",
      tools: EDITOR_JS_TOOLS(postId),
      minHeight: 0,
      data: initialData,
      autofocus: true,
      onReady: () => {
        console.log("Editor.js is ready to work!");
      },
      sanitizer: {
        mark: {
          class: true,
          style: true,
        },
        span: {
          class: true,
          style: true,
        },
      },
    });

    editorInstance.current = editor;
    if (instanceRef) instanceRef.current = editor;

    return () => {
      const instance = editorInstance.current;
      editorInstance.current = null;
      if (instanceRef) instanceRef.current = null;

      if (!instance || typeof instance.destroy !== "function") {
        return;
      }

      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }

      window.getSelection()?.removeAllRanges();

      void instance.isReady
        .catch(() => undefined)
        .then(() => {
          try {
            instance.destroy();
          } catch (e) {
            console.error("Editor cleanup error", e);
          }
        });
    };
  }, []);

  // 붙여넣기 등으로 유입되는 인라인 "글자 크기" 계열 스타일을 제거해 글씨 크기 통일
  useEffect(() => {
    const holder = document.getElementById("editorJS");
    if (!holder) return;

    // 제거 대상: 크기/폰트 계열만 (색/배경은 유지)
    const SIZE_PROPS = [
      "font-size",
      "line-height",
      "font-family",
      "font",
      "letter-spacing",
      "word-spacing",
    ];

    const cleanEl = (el: HTMLElement) => {
      const style = el.style;
      if (!style || style.length === 0) return;
      SIZE_PROPS.forEach((p) => style.removeProperty(p));
      // style 이 비면 속성 자체 제거
      if (!el.getAttribute("style")?.trim()) el.removeAttribute("style");
    };

    const cleanTree = (node: Node) => {
      if (!(node instanceof HTMLElement)) return;
      cleanEl(node);
      node.querySelectorAll<HTMLElement>("[style]").forEach(cleanEl);
    };

    // 새로 추가되는 노드(붙여넣기 결과 포함)만 정리 → 무한 루프 없음(attributes 미관찰)
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => cleanTree(n));
      }
    });
    observer.observe(holder, { childList: true, subtree: true });

    // 붙여넣기 직후 비동기 렌더까지 커버하도록 한 번 더 정리
    const onPaste = () => {
      requestAnimationFrame(() => cleanTree(holder));
      setTimeout(() => cleanTree(holder), 50);
    };
    holder.addEventListener("paste", onPaste, true);

    return () => {
      observer.disconnect();
      holder.removeEventListener("paste", onPaste, true);
    };
  }, []);

  // 인라인 툴바를 "선택 영역의 오른쪽 끝(right) 하단(bottom)"에 고정
  // - 기준을 mouseup 지점이 아니라 선택 영역 rect 로 잡아 드래그 방향과 무관하게 항상 오른쪽 끝 아래.
  // - Editor.js 가 표시 후에도 위치를 다시 잡으므로 MutationObserver 로 되돌리되,
  //   "현재 위치가 목표와 다를 때만" 다시 써서 피드백 루프 없이 안정적으로 고정한다.
  useEffect(() => {
    const OFFSET_X = 6;
    const OFFSET_Y = 8;

    const target = { active: false };
    let observer: MutationObserver | null = null;

    const getToolbar = () =>
      document.getElementById("editorJS")?.querySelector(".ce-inline-toolbar") as HTMLElement | null;

    // 툴바가 있어야 할 (부모 상대) 좌표 계산.
    // 여러 줄 선택 시 bounding box 의 right 는 가장 긴 줄 기준이라 실제 "끝"과 어긋난다.
    // Range 는 드래그 방향과 무관하게 start<=end 로 정규화되므로, getClientRects() 의
    // 마지막 rect(= 문서 순서상 마지막 줄의 선택 끝)를 기준으로 삼는다.
    const computeDesired = (toolbar: HTMLElement) => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return null;
      const range = selection.getRangeAt(0);
      const rects = range.getClientRects();
      let endRect: DOMRect | null = rects.length ? rects[rects.length - 1] : null;
      if (!endRect || (endRect.width === 0 && endRect.height === 0)) {
        endRect = range.getBoundingClientRect();
      }
      if (!endRect || (endRect.width === 0 && endRect.height === 0)) return null;
      const parent = (toolbar.offsetParent as HTMLElement | null) || document.getElementById("editorJS");
      if (!parent) return null;
      const pRect = parent.getBoundingClientRect();
      return {
        left: endRect.right - pRect.left + OFFSET_X,
        top: endRect.bottom - pRect.top + OFFSET_Y,
      };
    };

    const applyPosition = () => {
      const toolbar = getToolbar();
      if (!toolbar) return;
      const d = computeDesired(toolbar);
      if (!d) return;
      const curLeft = parseFloat(toolbar.style.left);
      const curTop = parseFloat(toolbar.style.top);
      // 목표와 다를 때만 재설정 → 쓴 값과 같으면 no-op 이라 루프가 생기지 않는다.
      if (Number.isNaN(curLeft) || Math.abs(curLeft - d.left) > 0.5) {
        toolbar.style.left = `${d.left}px`;
      }
      if (Number.isNaN(curTop) || Math.abs(curTop - d.top) > 0.5) {
        toolbar.style.top = `${d.top}px`;
      }
    };

    const ensureObserver = () => {
      if (observer) return;
      const toolbar = getToolbar();
      if (!toolbar) return;
      observer = new MutationObserver(() => {
        if (!target.active) return;
        applyPosition(); // Editor.js 가 옮기면 즉시 오른쪽 끝 하단으로 복귀
      });
      observer.observe(toolbar, { attributes: true, attributeFilter: ["style", "class"] });
    };

    const handleMouseUp = (e: MouseEvent) => {
      const editorEl = document.getElementById("editorJS");
      if (!editorEl || !(e.target instanceof Node) || !editorEl.contains(e.target)) return;
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
        target.active = false;
        return;
      }

      target.active = true;

      // 초기 표시 프레임들을 커버하며, 그 사이 observer 도 붙인다.
      let tries = 0;
      const apply = () => {
        ensureObserver();
        applyPosition();
        if (++tries < 8) requestAnimationFrame(apply);
      };
      requestAnimationFrame(apply);
    };

    // 툴바/색 메뉴 내부 조작(색 선택 등)은 위치 override 를 유지하고,
    // 그 외(에디터 본문에서 새 드래그 시작, 다른 곳 클릭 등)에서는 override 중단.
    const handleMouseDown = (e: MouseEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) {
        target.active = false;
        return;
      }
      const toolbar = getToolbar();
      if (toolbar && toolbar.contains(t)) return;
      const menu = document.querySelector(".cdx-color-menu");
      if (menu && menu.contains(t)) return;
      target.active = false;
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mousedown", handleMouseDown);
      if (observer) observer.disconnect();
    };
  }, []);

  const saveEditor = async () => {
    if (!editorInstance.current) return;
    const outputData = await editorInstance.current.save();
    await onSave(JSON.stringify(outputData));
  };

  return (
    <div
      style={{
        ...BoxStyle,
        margin: "0 auto",
        maxWidth: "60rem",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#fff",
          border: "1px solid rgba(0, 0, 0, 0.125)",
          borderRadius: "0.25rem",
        }}
      >
        <div
          style={{
            padding: "0.75rem 1.25rem",
            marginBottom: "0",
            backgroundColor: "#f7f7f9",
            borderBottom: "1px solid rgba(0, 0, 0, 0.125)",
          }}
        >
          내용
        </div>

        <div className="card-body" style={{ overflow: "visible" }}>
          <div className="card-text" style={{ minHeight: "300px" }}>
            <div id="editorJS" className="p-3"></div>
          </div>
        </div>
      </div>
      <div
        style={{
          position: "sticky",
          top: 0,
          textAlign: "right",
          display: "flex",
          gap: "0.5rem",
          justifyContent: "flex-end",
          zIndex: 100,
          backgroundColor: "#fff",
          padding: "0.5rem 0",
        }}
      >
        <Button type="primary" onClick={saveEditor}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default Editor;
